/**
 * api.js — PostgreSQL backend client
 * Connects to Node.js/Express backend at VUE_APP_API_URL (see @/config/apiBase).
 */

import { API_BASE_URL as BASE_URL, apiHeaders, fetchWithTimeout } from "@/config/apiBase";
import { clearAuthStorage } from "@/config/session";
import { isLoginGracePeriod } from "@/services/authSession";

function jsonApiHeaders(extra = {}) {
  return apiHeaders({ "Content-Type": "application/json", ...extra });
}

async function request(method, path, body, { retries = 0, timeoutMs } = {}) {
  const res = await fetchWithTimeout(`${BASE_URL}${path}`, {
    method,
    headers: jsonApiHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  }, timeoutMs);
  if (res.status === 503 && retries > 0) {
    let errMsg = "";
    try {
      const peek = await res.clone().json();
      errMsg = String(peek?.error || "");
    } catch (_) { /* ignore */ }
    if (/initializing/i.test(errMsg)) {
      await new Promise((r) => setTimeout(r, 2000));
      return request(method, path, body, { retries: retries - 1 });
    }
  }
  const text = await res.text();
  let json;
  try {
    json = text ? JSON.parse(text) : {};
  } catch (_) {
    let hint = "";
    if (res.status === 404) {
      hint = " API route not found — restart the backend (yarn dev) so the latest code is running on port 3000.";
    } else if (res.status >= 500) {
      hint = " Backend may be stopped or still starting — run yarn dev:clean in backend/ and check DB/VPN.";
    }
    throw new Error(`API returned invalid JSON (HTTP ${res.status}).${hint}`);
  }
  if (res.status === 401) {
    if (!isLoginGracePeriod()) {
      clearAuthStorage();
    }
    throw new Error(json.error || "Session expired. Please sign in again.");
  }
  if (!res.ok && json.error) throw new Error(json.error);
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error(
        `API route not found (404): ${path}. Restart the backend from backend/ (yarn dev:clean) so the latest code is running.`
      );
    }
    throw new Error(json.error || `Request failed (${res.status})`);
  }
  if (json.error) throw new Error(json.error);
  return json;
}

async function requestBlob(path) {
  const res = await fetchWithTimeout(`${BASE_URL}${path}`, { method: "GET", headers: apiHeaders() });
  if (!res.ok) {
    let errorMessage = `Request failed: ${res.status}`;
    try {
      const json = await res.json();
      if (json && json.error) errorMessage = json.error;
    } catch (_) {
      // ignore json parse errors
    }
    throw new Error(errorMessage);
  }

  const disposition = res.headers.get("content-disposition") || "";
  const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";\n]+)/i);
  const fileName = filenameMatch ? decodeURIComponent(filenameMatch[1].replace(/"/g, "")) : "report.pdf";
  const blob = await res.blob();
  return { blob, fileName };
}

const get = (path, params, opts = {}) => {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return request("GET", path + qs, undefined, { retries: 4, ...opts });
};
const post = (path, body, opts) => request("POST", path, body, opts);
const put  = (path, body, opts) => request("PUT",  path, body, opts);
const del  = (path, body) => request("DELETE", path, body);

// ─── Projects ────────────────────────────────────────────────────────────────
// DB table: projects
export const projectsApi = {
  getAll:  ()           => get("/api/projects"),
  insert:  (name)       => post("/api/projects", { name }),
  update:  (id, name)   => put(`/api/projects/${id}`, { name }),
  delete:  (id)         => del(`/api/projects/${id}`),
};

// ─── Sub Projects ─────────────────────────────────────────────────────────────
// DB table: sub_projects
export const subProjectsApi = {
  getAll:                (params)                 => get("/api/sub-projects", params),
  getByProject:          (projectId)              => get("/api/sub-projects", { projectId }),
  getByProjectActive:    (projectId)              => get("/api/sub-projects", { projectId, excludeDeployed: "true" }),
  insert:                (data)                   => post("/api/sub-projects", data),
  update:                (id, data)               => put(`/api/sub-projects/${id}`, data),
  delete:                (id)                     => del(`/api/sub-projects/${id}`),
};

// ─── Test Runs (table: public.test_run) ────────────────────────────────────────
export const testRunsApi = {
  getByProject: (projectId) => (
    projectId
      ? get("/api/test-runs", { projectId })
      : get("/api/test-runs")
  ),
  getById: (id) => get(`/api/test-runs/${id}`),
  insert: (data) => post("/api/test-runs", data),
  update: (id, updates) => put(`/api/test-runs/${id}`, updates),
  delete: (id) => del(`/api/test-runs/${id}`),
};

const TEST_RUN_DETAILS_TIMEOUT_MS = 180000;

// ─── Test Run Details (table: public.test_run_details) ─────────────────────────
export const testRunDetailsApi = {
  getByRun: (testRunId) => get("/api/test-run-details", { testRunId }, { timeoutMs: TEST_RUN_DETAILS_TIMEOUT_MS }),
  // items: [{ testCaseId, startDate, dueDate }]
  bulkInsert: (testRunId, items) =>
    post("/api/test-run-details/bulk", { testRunId, items }, { timeoutMs: TEST_RUN_DETAILS_TIMEOUT_MS }),
  replace: (testRunId, items) =>
    put("/api/test-run-details/replace", { testRunId, items }, { timeoutMs: TEST_RUN_DETAILS_TIMEOUT_MS }),
  renumberRunIds: (testRunId) =>
    post("/api/test-run-details/renumber", { testRunId }),
};

// ─── Test Plans ───────────────────────────────────────────────────────────────
export const testPlansApi = {
  getAll:  (params) => get("/api/test-plans", params),
  getById: (id)       => get(`/api/test-plans/${id}`),
  insert:  (data)     => post("/api/test-plans", data),
  update:  (id, data) => put(`/api/test-plans/${id}`, data),
  delete:  (id)       => del(`/api/test-plans/${id}`),
};

// ─── Project Plans (detail header) ────────────────────────────────────────────
export const projectPlansApi = {
  getById: (id) => get(`/api/project-plans/${id}`),
  getIdBySubProject: (subProjectId) => get(`/api/project-plan/by-sub-project/${subProjectId}`),
};

// ─── Test Plan Items (test_plan rows per project_plan) ────────────────────────
export const testPlanItemsApi = {
  getByProjectPlan: (projectPlanId) => get("/api/test-plan-items", { projectPlanId }),
  getById:          (id)            => get(`/api/test-plan-items/${id}`),
  insert:           (data)          => post("/api/test-plan-items", data),
  update:           (id, data)      => put(`/api/test-plan-items/${id}`, data),
  delete:           (id)            => del(`/api/test-plan-items/${id}`),
  getActivity:      (id)            => get(`/api/test-plan-items/${id}/activity`),
  addRemark:        (id, data)      => post(`/api/test-plan-items/${id}/remarks`, data),
};

// ─── Test Functions (public.tc_function table) ────────────────────────────────
export const testFunctionsApi = {
  getByProjectPlan: (projectPlanId) => get("/api/test-functions", { projectPlanId }),
  getBySubProject: (subProjectId) => get("/api/test-functions", { subProjectId }),
  getByProject: (projectId) => get("/api/test-functions", { projectId }),
  insert: (data) => post("/api/test-functions", data),
  update: (functionId, data) => put(`/api/test-functions/${functionId}`, data),
  delete: (functionId) => del(`/api/test-functions/${functionId}`),
};

// ─── Scenarios (real, public.scenario table) ──────────────────────────────────
export const scenariosApi = {
  getByProjectPlan: (projectPlanId) => get("/api/scenarios", { projectPlanId }),
  getBySubProject:  (subProjectId)  => get("/api/scenarios", { subProjectId }),
  insert: (data) => post("/api/scenarios", data),
  update: (scenarioId, data) => put(`/api/scenarios/${scenarioId}`, data),
};

// ─── Sub Scenarios (public.sub_scenario table) ────────────────────────────────
export const subScenariosApi = {
  getByScenario: (scenarioId) => get("/api/sub-scenarios", { scenarioId }),
  insert: (scenarioId, subScenarioName) => post("/api/sub-scenarios", { scenarioId, subScenarioName }),
};

// ─── Real Test Cases (public.test_case table) ──────────────────────────────────
export const realTestCasesApi = {
  getByProjectPlan: (projectPlanId) => get("/api/real-test-cases", { projectPlanId }),
  listProjects: () => get("/api/real-test-cases/by-project"),
  listSubProjects: (projectId) => get(`/api/real-test-cases/by-project/${projectId}/sub-projects`),
  listFunctions: (projectId) => get(`/api/real-test-cases/by-project/${projectId}/functions`),
  listSets: (projectId, subProjectId) =>
    get(`/api/real-test-cases/by-project/${projectId}/sub-projects/${subProjectId}/sets`),
  listFolders: (subProjectId, functionId) =>
    get("/api/test-case-folders", functionId ? { subProjectId, functionId } : { subProjectId }),
  createFolder: (data) => post("/api/test-case-folders", data),
  updateFolder: (folderId, data) => put(`/api/test-case-folders/${folderId}`, data),
  getFolderActivity: (folderId, subProjectId) =>
    get(`/api/test-case-folders/${folderId}/activity`, subProjectId ? { subProjectId } : undefined),
  deleteFolder: (folderId) => del(`/api/test-case-folders/${folderId}`),
  getByProject: (projectId, filters = {}) => {
    const params = {};
    if (filters.subProjectId) params.subProjectId = filters.subProjectId;
    if (filters.functionId) params.functionId = filters.functionId;
    if (filters.scenarioId) params.scenarioId = filters.scenarioId;
    if (filters.folderId) params.folderId = filters.folderId;
    return get(
      `/api/real-test-cases/by-project/${projectId}`,
      Object.keys(params).length ? params : undefined,
    );
  },
  getHistory: (id) => get(`/api/real-test-cases/${id}/history`),
  getActivity: (id) => get(`/api/real-test-cases/${id}/activity`),
  addRemark: (id, data) => post(`/api/real-test-cases/${id}/remarks`, data),
  updateState: (id, data) => put(`/api/real-test-cases/${id}/state`, data),
  getById: (id) => get(`/api/real-test-cases/${id}`),
  getTestRunLink: (id) => get(`/api/real-test-cases/${id}/test-run-link`),
  insert: (data) => post("/api/real-test-cases", data),
  update: (id, data) => put(`/api/real-test-cases/${id}`, data),
  delete: (id, data) => del(`/api/real-test-cases/${id}`, data),
};

// ─── Test Case Sync Requests ───────────────────────────────────────────────────
export const testCaseSyncApi = {
  getTargets: (testCaseId, projectPlanId) =>
    get("/api/test-case-sync/targets", { testCaseId, projectPlanId }),
  getRequests: (projectPlanId, status = "pending") =>
    get("/api/test-case-sync/requests", { projectPlanId, status }),
  getById: (id) => get(`/api/test-case-sync/requests/${id}`),
  getPendingByCase: (testCaseId) => get(`/api/test-case-sync/pending-by-case/${testCaseId}`),
  createRequest: (data) => post("/api/test-case-sync/requests", data),
  approve: (id) => post(`/api/test-case-sync/requests/${id}/approve`, {}),
  reject: (id, reason) => post(`/api/test-case-sync/requests/${id}/reject`, { reason }),
};

// ─── Steps and Result (public.steps_and_result table) ─────────────────────────
export const stepsAndResultApi = {
  getByCase: (testCaseId) => get("/api/steps-and-result", { testCaseId }),
  save: (testCaseId, steps, modifyBy) => post("/api/steps-and-result/save", { testCaseId, steps, modifyBy }),
};

// ─── Actual Result (public.actual_result table) ────────────────────────────────
export const actualResultApi = {
  getHistory: (testRunDetailId) => get("/api/actual-result/history", { testRunDetailId }),
};

// ─── My Tasks ─────────────────────────────────────────────────────────────────
export const myTasksApi = {
  getByUser: (userId) => get("/api/my-tasks", userId ? { userId } : undefined),
};

export const teamTasksApi = {
  getByUser: (userId) => get("/api/team-tasks", userId ? { userId } : undefined),
};

// ─── AI (Gemini) ───────────────────────────────────────────────────────────────
export const aiApi = {
  analyzeRequirements: (body) => post("/api/ai/analyze-requirements", body),
  generateOverallQuality: (body) => post("/api/ai/generate-overall-quality", body),
  listPrompts: () => get("/api/ai/prompts"),
  updatePrompt: (key, body) => put(`/api/ai/prompts/${key}`, body),
};

// ─── Reports ───────────────────────────────────────────────────────────────────
export const reportsApi = {
  getTestReport: (testRunId) => get("/api/test-report", { testRunId }),
  downloadTestCasePdf: (projectPlanId, extra = {}) =>
    requestBlob(`/api/reports/test-cases/pdf?${new URLSearchParams({
      ...(projectPlanId ? { projectPlanId } : {}),
      ...extra,
    }).toString()}`),
  downloadTestRunPdf: (testRunId) =>
    requestBlob(`/api/reports/test-runs/pdf?${new URLSearchParams({ testRunId }).toString()}`),
  downloadTestCaseExcel: (projectPlanId, extra = {}) =>
    requestBlob(`/api/reports/test-cases/excel?${new URLSearchParams({
      ...(projectPlanId ? { projectPlanId } : {}),
      ...extra,
    }).toString()}`),
  downloadTestReportPdf: (projectPlanId, testRunId, reportType) =>
    requestBlob(
      `/api/reports/test-cases/pdf?${new URLSearchParams({
        projectPlanId,
        ...(testRunId ? { testRunId } : {}),
        ...(reportType ? { reportType } : {}),
      }).toString()}`
    ),
  nextGeneratedReportNumber: () => get("/api/qa-generated-reports/next-number"),
  listGeneratedReports: () => get("/api/qa-generated-reports"),
  saveGeneratedReport: (body) => post("/api/qa-generated-reports", body, { timeoutMs: 180000 }),
  downloadGeneratedReportPdf: (id) => requestBlob(`/api/qa-generated-reports/${id}/pdf`),
};

// ─── Users ────────────────────────────────────────────────────────────────────
export const usersApi = {
  getAll: () => get("/api/users"),
  getPendingRegistrations: () => get("/api/users/pending-registrations"),
  insert: (data) => post("/api/users", data),
  update: (id, data) => put(`/api/users/${id}`, data),
  delete: (id) => del(`/api/users/${id}`),
  approve: (id) => post(`/api/users/${id}/approve`, {}),
  requestPasswordReset: (id) => post(`/api/users/${id}/force-password-reset`, {}),
};

export const rolesApi = {
  getAll: () => get("/api/roles"),
  insert: (roleName) => post("/api/roles", { roleName }),
  update: (id, data) => put(`/api/roles/${id}`, data),
  delete: (id) => del(`/api/roles/${id}`),
};

export const menuSettingsApi = {
  getAll: () => get("/api/menu-settings"),
  update: (menuId, data) => put(`/api/menu-settings/${menuId}`, data),
};

export const rolePermissionsApi = {
  getByRole: (roleId, extra = {}) => get("/api/role-permissions", { roleId, ...extra }),
  saveByRole: (roleId, permissions) => put(`/api/role-permissions/${roleId}`, { permissions }),
};

export const featureSettingsApi = {
  getAll: () => get("/api/feature-settings"),
};

export const roleFeaturePermissionsApi = {
  getByRole: (roleId, extra = {}) => get("/api/role-feature-permissions", { roleId, ...extra }),
  saveByRole: (roleId, permissions) => put(`/api/role-feature-permissions/${roleId}`, { permissions }),
};

export const uiMenuApi = {
  getByUser: (userId) => get("/api/ui/menu-access", userId ? { userId } : undefined),
};

export const uiFeatureApi = {
  getByUser: (userId) => get("/api/ui/feature-access", userId ? { userId } : undefined),
};

export { apiHeaders, jsonApiHeaders };

export const automationApi = {
  getSummary: () => get("/api/automation/summary"),
  getEnvironments: () => get("/api/automation/environments"),
  getRepos: () => get("/api/automation/repos"),
  getRuns: (params) => get("/api/automation/runs", params),
  getRunById: (id) => get(`/api/automation/runs/${id}`),
  triggerRun: (body) => post("/api/automation/runs", body),
};

export const sprintSettingsApi = {
  getAll: () => get("/api/sprint-settings"),
  getByTeam: (team) => get("/api/sprint-settings", { team }),
  create: (data) => post("/api/sprint-settings", data),
  update: (id, data) => put(`/api/sprint-settings/${id}`, data),
  delete: (id) => del(`/api/sprint-settings/${id}`),
};

export const dashboardApi = {
  getTeamSprintSummary: () => get("/api/dashboard/team-sprint-summary"),
  getTeamSprintCalendar: (team) => get("/api/dashboard/team-sprint-calendar", { team }),
};

